/**
 * Coach + player profile types. Central source of truth for the
 * progressive-disclosure profile UX and the data shape the Brain reads
 * when tuning its tone / personalisation.
 *
 * Two kinds of tiering:
 *   - Coach LEVEL  — a single ordered enum that drives comms/verbosity
 *     (novice -> elite). Used to gate advanced UI and phrase the Brain
 *     prompt.
 *   - Player TIERS — additive depth buckets (quick → basic → scouting →
 *     deep). A profile can sit at any tier; we compute a % from which
 *     fields are filled rather than a single "level".
 *
 * Keep the helpers pure and side-effect-free; UI + Brain both import.
 */

// ============================================================================
// Coach
// ============================================================================

export const COACH_LEVELS = [
  'NOVICE',
  'GRASSROOTS',
  'COMPETITIVE',
  'ADVANCED',
  'ELITE_CAT1',
] as const;
export type CoachLevel = typeof COACH_LEVELS[number];

export const COACH_LEVEL_ORDER: Record<CoachLevel, number> = {
  NOVICE: 0,
  GRASSROOTS: 1,
  COMPETITIVE: 2,
  ADVANCED: 3,
  ELITE_CAT1: 4,
};

export const COACH_LEVEL_LABEL: Record<CoachLevel, string> = {
  NOVICE: 'Novice',
  GRASSROOTS: 'Grassroots',
  COMPETITIVE: 'Competitive',
  ADVANCED: 'Advanced',
  ELITE_CAT1: 'Elite / Cat 1',
};

export const COACH_LEVEL_DESC: Record<CoachLevel, string> = {
  NOVICE: 'New to coaching. Want guided prompts and explanations.',
  GRASSROOTS: 'Coaching recreationally. Comfortable with basics.',
  COMPETITIVE: 'Club / travel team. Fluent with positional play and pressing.',
  ADVANCED: 'Licensed / performance level. Methodology-driven.',
  ELITE_CAT1: 'Academy / pro pathway. Peer-level dialogue expected.',
};

export type CommsStyle = 'concise' | 'standard' | 'scaffolded';

export const COMMS_STYLE_LABEL: Record<CommsStyle, string> = {
  concise: 'Concise — skip the explanations',
  standard: 'Standard — balanced detail',
  scaffolded: 'Scaffolded — explain terms + examples',
};

/** Default comms style per level. Coaches can override in profile. */
export function defaultCommsFor(level: CoachLevel | null | undefined): CommsStyle {
  switch (level) {
    case 'NOVICE':      return 'scaffolded';
    case 'GRASSROOTS':  return 'scaffolded';
    case 'COMPETITIVE': return 'standard';
    case 'ADVANCED':    return 'concise';
    case 'ELITE_CAT1':  return 'concise';
    default:            return 'standard';
  }
}

export interface CoachProfile {
  coach_level: CoachLevel | null;
  years_coaching: number | null;
  age_groups_coached: string[];
  coach_badges: string[];
  coach_methodology: string | null;
  coach_comms_style: CommsStyle | null;
  coach_profile_extra: Record<string, unknown>;
  coach_profile_pct: number;
  /** Founder sync 2026-04-30 — voice OUTPUT (TTS) preferences.
   *  Default semantics (when columns/rows are missing): voice_enabled
   *  = TRUE (voice OUT is core), voice_rate = 1.0, voice_id = NULL
   *  (use system default voice for the user's lang). */
  voice_enabled?: boolean | null;
  voice_rate?: number | null;
  voice_id?: string | null;
  /** Founder sync follow-up — TTS provider. 'system' uses the OS
   *  engine (Web Speech API / expo-speech); 'cloud' routes through the
   *  Supabase tts edge function → OpenAI tts-1-hd. Default 'system'. */
  voice_provider?: 'system' | 'cloud' | null;
}

/** Compute a completeness percentage from a coach profile — same
 *  calculation used by the pill and the settings progress bar. */
export function computeCoachProfilePct(p: Partial<CoachProfile>): number {
  const filled = [
    !!p.coach_level,
    p.years_coaching != null,
    (p.age_groups_coached?.length ?? 0) > 0,
    (p.coach_badges?.length ?? 0) > 0,
    !!p.coach_methodology && p.coach_methodology.trim().length > 0,
    !!p.coach_comms_style,
  ].filter(Boolean).length;
  return Math.round((filled / 6) * 100);
}

/** Ordered comparator for gating UI: isAtLeast('COMPETITIVE', myLevel). */
export function isAtLeast(min: CoachLevel, have: CoachLevel | null | undefined): boolean {
  if (!have) return false;
  return COACH_LEVEL_ORDER[have] >= COACH_LEVEL_ORDER[min];
}

// Common option lists for UI pickers — keep here so web + mobile agree
export const COACH_BADGE_OPTIONS = [
  'USSF F', 'USSF E', 'USSF D', 'USSF C', 'USSF B', 'USSF A',
  'UEFA C', 'UEFA B', 'UEFA A', 'UEFA Pro',
  'NSCAA Premier', 'NSCAA Advanced National',
  'FA Level 1', 'FA Level 2', 'FA Youth Module 1-3',
  'CSA A', 'CSA B', 'CSA C',
] as const;

export const AGE_GROUP_OPTIONS = [
  'U6', 'U7', 'U8', 'U9', 'U10', 'U11', 'U12',
  'U13', 'U14', 'U15', 'U16', 'U17', 'U18', 'U19', 'U19+',
] as const;

export const METHODOLOGY_OPTIONS = [
  'US Soccer PDF',
  'FA 4-Corner Model',
  'Cruyff / Barcelona',
  'Bielsa / intensity',
  'Guardiola / positional',
  'Dutch Total Football',
  'German (relationship play)',
  'Own — eclectic',
] as const;

/**
 * Prompt fragment that direct-AI generators (PhilosophyHub, CurriculumView,
 * TrainingCalendar, DrillAuthor, VideoAnalysis) append to their system
 * prompts so the output tone matches the coach's level + comms pref —
 * without dragging the Brain's full COMMS_GUIDE into every surface.
 * Keep it compact; these prompts already burn a lot of tokens.
 *
 * Wave A.3 — when `brain_rating` is supplied AND it diverges from the
 * self-declared level's expected band, the hint nudges the tone one
 * step in the demonstrated direction. Conservative: only nudges, never
 * overrides. Coach can still rate themselves anywhere they like; the
 * hint just gives the model permission to use slightly more / less
 * sophisticated language than the self-rating alone implies.
 */
export function buildCoachStyleHint(p: {
  level: CoachLevel | null;
  comms: CommsStyle;
  /** 0-100 from coach_evaluations.brain_rating (cached on
   *  users.coach_brain_rating). When omitted, only the level + comms
   *  drive the hint — exactly the v1 behaviour. */
  brain_rating?: number | null;
} | null): string {
  if (!p) return '';

  // Effective level — nudges by brain rating when the demonstrated
  // craft diverges from the self-declared level. Stays within 1
  // step of self-declared so the coach's identity isn't overridden.
  const eff: CoachLevel | null = (() => {
    if (p.level == null || p.brain_rating == null) return p.level;
    const order = COACH_LEVEL_ORDER[p.level];
    // Brain rating bands mirror coachEvaluation.ts's mapping.
    const r = p.brain_rating;
    const want = r > 90 ? 4 : r > 72 ? 3 : r > 50 ? 2 : r > 25 ? 1 : 0;
    if (want > order + 1) {
      const target = order + 1;
      return COACH_LEVELS.find((lv) => COACH_LEVEL_ORDER[lv] === target) ?? p.level;
    }
    if (want < order - 1) {
      const target = order - 1;
      return COACH_LEVELS.find((lv) => COACH_LEVEL_ORDER[lv] === target) ?? p.level;
    }
    return p.level;
  })();

  const depth = eff === 'ELITE_CAT1' || eff === 'ADVANCED'
    ? 'Dense and specific — peer-level coaching language. No definitions. Reference methodology when relevant.'
    : eff === 'COMPETITIVE'
    ? 'Standard coaching vocabulary. Brief explanations where novel. Assume fluency with FA 4-corner + positional play.'
    : // NOVICE / GRASSROOTS / unknown
      'Define jargon inline the first time. Prefer concrete examples over abstract theory. One clear choice over a menu.';

  const width = p.comms === 'concise'
    ? 'Short. Bullets where possible. Skip the recap.'
    : p.comms === 'scaffolded'
    ? 'Walk through reasoning. Offer an example per concept.'
    : 'Balanced detail.';

  const ratingNote = p.brain_rating != null && eff !== p.level
    ? `\n- Coach is self-declared ${p.level}; recent craft sits closer to ${eff} (rating ${p.brain_rating}/100). Pitch one step toward demonstrated.`
    : '';

  return `COACH STYLE HINT (tune output to this):
- ${depth}
- ${width}${ratingNote}`;
}

/** Lightweight derivation from a partial CoachProfile — mirrors the
 *  `defaultCommsFor` fallback so direct generators pick the same comms
 *  the Brain did without importing the full hook.
 *
 *  Wave A.3 — accepts an optional brain_rating so callers passing
 *  the cached users.coach_brain_rating get it back through to
 *  buildCoachStyleHint. Strictly additive; old call sites that
 *  passed only the profile keep working. */
export function resolveCoachStyle(
  profile: Partial<CoachProfile> | null | undefined,
  brainRating?: number | null,
): {
  level: CoachLevel | null;
  comms: CommsStyle;
  brain_rating?: number | null;
} {
  const level = (profile?.coach_level ?? null) as CoachLevel | null;
  const comms = (profile?.coach_comms_style ?? defaultCommsFor(level)) as CommsStyle;
  return { level, comms, brain_rating: brainRating ?? null };
}

// ============================================================================
// Player
// ============================================================================

export const PLAYER_TIERS = ['QUICK', 'BASIC', 'SCOUTING', 'DEEP'] as const;
export type PlayerTier = typeof PLAYER_TIERS[number];

export const PLAYER_TIER_LABEL: Record<PlayerTier, string> = {
  QUICK: 'Quick',
  BASIC: 'Basic',
  SCOUTING: 'Scouting',
  DEEP: 'Deep scouting',
};

/**
 * Packed player profile (lives in users.player_profile as JSONB).
 * Top-level keys = tiers beyond Quick (Quick fields live on the users
 * row / team_players junction directly).
 *
 * Wave C — Coach feedback added background + preferred_positions to
 *          surface "context for the player at a glance" alongside
 *          the existing measurements.
 */
export interface PlayerProfileBasic {
  height_cm?: number | null;
  weight_kg?: number | null;
  years_playing?: number | null;
  parent_contact?: string | null;
  dob?: string | null; // YYYY-MM-DD
  /** Free-form context — left foot, school team, joined mid-season,
   *  ex-academy, sister also plays, etc. Coach reads at a glance. */
  background?: string | null;
  /** Optional position preferences ordered most-to-least. Distinct
   *  from team_players.position (the slot the coach has assigned);
   *  this is what the player WANTS to play. */
  preferred_positions?: string[];
}

export interface PlayerProfileScouting {
  strengths: string[];
  dev_areas: string[];
  baseline: {
    technical_tactical?: number | null; // 0-100
    physical?: number | null;
    psychological?: number | null;
    social?: number | null;
  };
  injury_notes?: string | null;
  availability?: string | null;
}

export interface PlayerProfileDeep {
  video_evidence: Partial<Record<'technical_tactical' | 'physical' | 'psychological' | 'social', string[]>>;
  peer_benchmark_note?: string | null;
  trajectory_hypothesis?: string | null;
  recruiting_target?: 'NONE' | 'ACADEMY' | 'COLLEGE_D1' | 'COLLEGE_D2_D3' | 'NAIA_JUCO' | null;
  formation_role_fit?: string | null;
  psych_social_notes?: string | null;
}

export interface PlayerProfile {
  basic?: PlayerProfileBasic;
  scouting?: PlayerProfileScouting;
  deep?: PlayerProfileDeep;
}

/** Rough weights per tier — Quick is implicit (name + position on
 *  team_players; assumed present if the player exists). Basic counts
 *  as 20%, Scouting as 45%, Deep as 35% of the total. Each tier is
 *  filled proportionally by how many of its own fields are populated. */
export function computePlayerProfilePct(p: PlayerProfile | null | undefined): number {
  if (!p) return 0;
  const pct = (() => {
    // Basic — 4 fields
    const b = p.basic ?? {};
    const basicFields = [b.height_cm, b.weight_kg, b.years_playing, b.parent_contact];
    const basicPct = basicFields.filter(v => v != null && v !== '').length / 4;

    // Scouting — strengths/dev_areas + 4 baseline scores + injury/avail
    const s = p.scouting ?? { strengths: [], dev_areas: [], baseline: {} };
    const scoutFields = [
      (s.strengths?.length ?? 0) > 0,
      (s.dev_areas?.length ?? 0) > 0,
      s.baseline?.technical_tactical != null,
      s.baseline?.physical != null,
      s.baseline?.psychological != null,
      s.baseline?.social != null,
      !!s.injury_notes,
      !!s.availability,
    ];
    const scoutPct = scoutFields.filter(Boolean).length / 8;

    // Deep — 6 fields
    const d = p.deep ?? { video_evidence: {} };
    const deepFields = [
      Object.values(d.video_evidence ?? {}).some(v => (v?.length ?? 0) > 0),
      !!d.peer_benchmark_note,
      !!d.trajectory_hypothesis,
      d.recruiting_target != null && d.recruiting_target !== 'NONE',
      !!d.formation_role_fit,
      !!d.psych_social_notes,
    ];
    const deepPct = deepFields.filter(Boolean).length / 6;

    return (basicPct * 20) + (scoutPct * 45) + (deepPct * 35);
  })();
  return Math.max(0, Math.min(100, Math.round(pct)));
}

/** Map a computed pct to a human tier label — used by roster pills. */
export function tierForPct(pct: number): PlayerTier {
  if (pct >= 75) return 'DEEP';
  if (pct >= 45) return 'SCOUTING';
  if (pct >= 15) return 'BASIC';
  return 'QUICK';
}
